import React, { useMemo } from 'react';
import { useProvider } from '~/Hooks';
import { Class } from '~/Components/Icons/eventTypes/Class';
import { useTranslation } from 'react-i18next';
import { IItemCreationSubsectionInputProps } from '~/Components';
import { ExtendedEventGroup } from '~/Screens/Events/EventCreate/ExtendedEventGroup.model';
import { ItemCreationValidInput } from '~/Components/ItemCreation/ItemCreationSubsection/ItemCreationSubsectionInput.model';
import { FormName } from '~/Components/Icons/form/FormName';
import { MapIcon, Spots, Camera } from '~/Components/Icons';
import { EventCreateSectionStyles } from './EventCreateSectionStyles';
import { IBookingSummaryEvent } from '~/Models/DTO/Booking.models';
import { EventType } from '~/Models/DTO/Event.model';
import { ExtendedEvent } from '~/Screens/Events/EventCreate/ExtendedEvent.model';
import { Typography } from '~/UIKit';

export interface IUseGeneralSectionInputsProps {
  eventType: EventType;
  isLoading: boolean;
  eventGroup: ExtendedEventGroup;
  event?: ExtendedEvent;
  bookingSummary?: IBookingSummaryEvent;
  liveStreamEnabled?: boolean;
}

export const useGeneralSectionInputs = ({
  eventType,
  isLoading,
  eventGroup,
  event,
  bookingSummary,
  liveStreamEnabled,
}: IUseGeneralSectionInputsProps): IItemCreationSubsectionInputProps<ExtendedEventGroup>[] => {
  const { t } = useTranslation();

  const disbaleLink = useMemo(() => {
    const today = new Date();
    const classDate = new Date(new Date(event?.originalStart?.toString() || 0));
    const sameDay = today.toLocaleDateString() === classDate.toLocaleDateString();
    if (!sameDay || !event?.virtualClassUrl) {
      return false;
    }
    const diffMs = classDate.getTime() - today.getTime();
    const diffMins = Math.round(diffMs / 1000 / 60); // minutes
    return diffMins < 30;
  }, [event?.startTime]);

  const { isUsingThePartnerTool } = useProvider();
  const { classes } = EventCreateSectionStyles();
  const input: { [key: string]: IItemCreationSubsectionInputProps<any> } = useMemo(
    () => ({
      name: {
        icon: <Class className={classes.eventCreateSectionClassIcon} />,
        label: `${t('form.field.title')}*`,
        placeholder: t('form.placeholder.title'),
        type: ItemCreationValidInput.TEXT,
        name: 'name',
        noIconBackground: true,
        InputProps: { inputProps: { maxLength: 50 } },
        tooltip: isUsingThePartnerTool && {
          title: t('partner.event.create.tooltip.title'),
          placement: 'bottom-start',
        },
      },
      trainer: {
        icon: <FormName />,
        label: `${t('form.field.trainer')}*`,
        placeholder: t('form.placeholder.trainer'),
        type: ItemCreationValidInput.TRAINER_SELECTOR,
        name: 'trainerIds',
        disabled: isLoading,
      },
      location: {
        icon: <MapIcon />,
        label: t('form.field.location'),
        placeholder: t('form.placeholder.location'),
        type: ItemCreationValidInput.LOCATION_SELECTOR,
        name: 'locationId',
        disabled: (isUsingThePartnerTool && !!eventGroup?.id) || false,
      },
      livestream: {
        icon: (
          <span className={classes.castIcon}>
            <Camera viewBox="0 0 24 24" />
          </span>
        ),
        tooltip: !event
          ? {
              title: t('form.field.livestream.tooltip'),
              placement: 'bottom-start',
            }
          : undefined,
        label: t('form.field.livestream'),
        type: ItemCreationValidInput.SWITCHER,
        name: 'livestream',
        disabled: !!event,
        note:
          !event && eventGroup?.livestream ? (
            <div className={classes.note}>
              <Typography>{t('form.field.livestream.note')}</Typography>
            </div>
          ) : undefined,
      },
      liveStreamLink: {
        label: t('app.livestream.link'),
        type: ItemCreationValidInput.TEXT,
        name: 'virtualClassUrl',
        disabled: disbaleLink,
        note: !!event ? (
          <div className={classes.note}>
            <Typography>{t('form.field.livestreamlink.note')}</Typography>
          </div>
        ) : undefined,
      },
      seats: {
        icon: <Spots />,
        label: `${t('form.field.spots')}*`,
        type: ItemCreationValidInput.NUMBER,
        name: 'seats',
        hasAnError: (eventGroup.seats || 0) < (bookingSummary?.bookingsBooked || 0),
      },
    }),
    [eventGroup, bookingSummary, isLoading, isUsingThePartnerTool, t],
  );

  if (!eventType || isLoading) {
    return [];
  } else if (eventType === EventType.FREE_TRAINING) {
    return [input.name, input.location];
  }

  const fields = [input.name, input.trainer, input.location];
  if (liveStreamEnabled) {
    fields.push(input.livestream);
  }
  if (liveStreamEnabled && !!event) {
    fields.push(input.liveStreamLink);
  }
  fields.push(input.seats);
  return fields;
};
